import { Fragment, type ReactNode } from 'react';
import { Link } from '@backstage/core-components';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { issueUrl, type McpServer, type ServerState } from './servers';

/**
 * The servers of the MCP & CLI page (specs/mcp-and-cli.md AC-4, AC-10): one
 * row per server — its name and address, what it offers, how a client signs
 * in, its state, and where it is set up.
 *
 * A table where there is room, and one entry per server on a phone, where four
 * columns side by side would push the page sideways.
 */

/** A row's state, including the two only the portal's own server can be in. */
export type RowState =
  | ServerState
  | { kind: 'asking' }
  | {
      kind: 'unknown';
      /** Why, in a sentence for the reader. */
      reason: string;
    };

export type ServerRow = Omit<McpServer, 'offers' | 'state'> & {
  offers: ReactNode;
  state: RowState;
};

const useStyles = makeStyles(theme => ({
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
    '& thead th': {
      textAlign: 'left',
      fontWeight: 500,
      fontSize: '0.8125rem',
      color: 'var(--ui-text-muted)',
      padding: theme.spacing(1, 2),
      borderBottom: '1px solid var(--ui-border)',
    },
    // The tools take what the other three leave: the server column wraps its
    // address at slashes and dots, the sign-in and the state are short.
    '& thead th:nth-child(1)': { width: '30%' },
    '& thead th:nth-child(3)': { width: '8rem' },
    '& thead th:nth-child(4)': { width: '9.5rem' },
    '& td, & tbody th': {
      textAlign: 'left',
      fontWeight: 'inherit',
      verticalAlign: 'top',
      padding: theme.spacing(1.5, 2),
      borderBottom: '1px solid var(--ui-border)',
    },
    '& tbody tr:last-child > *': { borderBottom: 'none' },
  },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid var(--ui-border)',
    '&:last-child': { borderBottom: 'none' },
    fontSize: '0.875rem',
  },
  itemHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  name: { fontWeight: 600, margin: 0, fontSize: '0.9375rem' },
  address: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
  },
  muted: { color: 'var(--ui-text-muted)' },
  setup: { fontSize: '0.8125rem' },
  toolNames: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8125rem',
    overflowWrap: 'anywhere',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    padding: theme.spacing(0.25, 1),
    borderRadius: 'var(--radius-pill)',
    fontSize: '0.75rem',
    fontWeight: 500,
    border: '1px solid',
  },
  // The green the portal's other pages give a good state (OutcomePill,
  // DeploysList, CiHealthOverview), so Live reads the same everywhere.
  live: {
    color: '#2EA043',
    background: 'rgba(46, 160, 67, 0.15)',
    borderColor: 'rgba(46, 160, 67, 0.4)',
  },
  planned: {
    color: 'var(--ui-primary)',
    background: 'var(--ui-nav-active-bg)',
    borderColor: 'var(--ui-primary)',
  },
  unknown: {
    color: 'var(--ui-warn-text)',
    background: 'var(--ui-warn-bg)',
    borderColor: 'var(--ui-warn-border)',
  },
}));

/**
 * Tool names, in code type so they read as names to call rather than as a
 * description — the portal's own and those of a server written down alike.
 */
export const ToolNames = ({ names }: { names: string[] }) => {
  const classes = useStyles();
  return <span className={classes.toolNames}>{names.join(', ')}</span>;
};

/** A server as a row, what it offers set as names or as a phrase. */
export const rowOf = (server: McpServer): ServerRow => ({
  ...server,
  offers:
    'tools' in server.offers ? (
      <ToolNames names={server.offers.tools} />
    ) : (
      server.offers.summary
    ),
});

/** The state, as a chip; a planned one links the issue that builds it. */
export const StateChip = ({ state }: { state: RowState }) => {
  const classes = useStyles();
  switch (state.kind) {
    case 'live':
      return <span className={`${classes.chip} ${classes.live}`}>Live</span>;
    case 'planned':
      return (
        <Link
          to={issueUrl(state.issue)}
          className={`${classes.chip} ${classes.planned}`}
        >
          Planned · {state.issue}
        </Link>
      );
    case 'asking':
      return (
        <span className={`${classes.chip} ${classes.unknown}`}>Asking…</span>
      );
    default:
      return (
        <span
          className={`${classes.chip} ${classes.unknown}`}
          title={state.reason}
        >
          Unknown
        </span>
      );
  }
};

/**
 * Text holding an address, which may break after a slash or a dot — where a
 * reader expects a line to break — rather than in the middle of a word. Used
 * for the servers' addresses and for the command that names one.
 */
export const Breakable = ({ text }: { text: string }) => (
  <>
    {text.split(/(?<=[/.])/).map((part, i) => (
      <Fragment key={i}>
        {i > 0 && <wbr />}
        {part}
      </Fragment>
    ))}
  </>
);

const Setup = ({ row }: { row: ServerRow }) => {
  const classes = useStyles();
  if (!row.setup) return null;
  return (
    <span className={classes.setup}>
      Set up: <Link to={row.setup.href}>{row.setup.label}</Link>
    </span>
  );
};

export const ServerList = ({ rows }: { rows: ServerRow[] }) => {
  const classes = useStyles();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));

  if (narrow) {
    return (
      <ul className={classes.list} aria-label="MCP servers">
        {rows.map(row => (
          <li key={row.id} className={classes.item}>
            <div className={classes.itemHead}>
              <h3 className={classes.name}>{row.name}</h3>
              <StateChip state={row.state} />
            </div>
            {row.address && (
              <span className={classes.address}>
                <Breakable text={row.address} />
              </span>
            )}
            <span>{row.offers}</span>
            <span className={classes.muted}>Sign-in: {row.signIn}</span>
            <Setup row={row} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <table className={classes.table} aria-label="MCP servers">
      <thead>
        <tr>
          <th scope="col">Server</th>
          <th scope="col">Tools</th>
          <th scope="col">Sign-in</th>
          <th scope="col">State</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id}>
            <th scope="row">
              <div className={classes.name}>{row.name}</div>
              {row.address && (
                <div className={classes.address}>
                  <Breakable text={row.address} />
                </div>
              )}
              <Setup row={row} />
            </th>
            <td>{row.offers}</td>
            <td className={classes.muted}>{row.signIn}</td>
            <td>
              <StateChip state={row.state} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
